import React, { useEffect, useState } from "react";
import { Typography, Row, Col, Card, Button, Collapse, Switch, InputNumber } from "antd";
import {
  CheckOutlined,
  TagFilled,
  FireOutlined,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";
import "./Plans.scss";
import FeatureComparisonTable from "../FeatureComparisonTable/FeatureComparisonTable";
import * as ENVIRONMENT from '../../../../env.js'

const { Paragraph } = Typography;
const allowedKeys = [
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
];

// Mobile Card Components
const MobilePackageCards = ({ expanded, setExpanded, annual, selectedPlan, setSelectedPlan }) => {
  const utm_source = localStorage.getItem("utm_source") || "";
  const [expandedCards, setExpandedCards] = useState({});

  const toggleCard = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const subscriptions = [
    {
      for: "For anyone",
      name: "Free Trial",
      type: "subscription",
      package_type: "free_trial_ai",
      desc: "See what's possible",
      price: "$0",
      price_monthly: "$0",
      price_time: "",
      price_details: "14-day limited trial",
      price_details_monthly: "14-day limited trial",
      discount_statement: "",
      discount_statement_monthly: "",
      switch_to_subscription: "",
      button_text: "Start your free trial",
      credits: "",
      credits_monthly: "",

    },
    {
      for: "For new sellers",
      name: "ALL3D Starter",
      type: "subscription",
      package_type: "starter_ai",
      desc: "60 credits a month. Approx. 15 images a month",
      price: "$525",
      price_monthly: "$630",
      price_time: "/month",
      price_details: "Billed annually. $35 per image",
      price_details_monthly: "Billed monthly. $42 per image",
      discount_statement: "27% the bundle price",
      discount_statement_monthly: "14% off the bundle price",
      switch_to_subscription: "",
      button_text: "Sign up for Starter",
      credits: "All 960 credits available at once. 4 credits per final image.  Unlimited 1K generations.",
      credits_monthly: "60 credits available monthly. 4 credits per final image. Unlimited 1K generations.",
    },
    {
      for: "For growing sellers",
      name: "ALL3D Growth",
      isPopular: true,
      type: "subscription",
      package_type: "growth_ai",
      desc: "100 credits a month. Approx. 25 images a month",
      price: "$875",
      price_monthly: "$1050",
      price_time: "/month",
      price_details: "Billed annually. $35 per image",
      price_details_monthly: "Billed monthly. $42 per image",
      discount_statement: "27% off the bundle price",
      discount_statement_monthly: "14% off the bundle price",
      switch_to_subscription: "",
      button_text: "Sign up for Growth",
      credits: "All 1200 credits available at once. 4 credits per final image.  Unlimited 1K generations.",
      credits_monthly: "100 credits available monthly. 4 credits per final image. Unlimited 1K generations.",
    },
    {
      for: "For pro sellers",
      name: "ALL3D Pro",
      type: "subscription",
      package_type: "pro_ai",
      desc: "200 credits a month. Approx. 50 images a month",
      credits: "12 credits per image. All 180 credits available at once.",
      price: "$1,750",
      price_monthly: "$2100",
      price_time: "/month",
      price_details: "Billed annually. $35 per image",
      price_details_monthly: "Billed monthly. $42 per image",
      discount_statement: "27% off the bundle price",
      discount_statement_monthly: "14% off the bundle price",
      switch_to_subscription: "",
      button_text: "Sign up for Pro",
      credits: "All 2400 credits available at once. 4 credits per final image.  Unlimited 1K generations.",
      credits_monthly: "200 credits available monthly. 4 credits per final image. Unlimited 1K generations.",
    },
    {
      for: "For large teams",
      name: "ALL3D Enterprise",
      type: "subscription",
      package_type: "enterprise",
      desc: "Custom plan with 3D/AR",
      credits: "Contact us",
      price: "Let's talk",
      price_monthly: "Let's talk",
      price_details: "Schedule a time to discuss",
      price_details_monthly: "Schedule a time to discuss",
      discount_statement: "",
      discount_statement_monthly: "",
      switch_to_subscription: "",
      button_text: "Schedule a demo",
      credits: <a href="/contact-us" className='manrope f-12 w-500 lh-20 blue'>Contact us</a>,
      credits_monthly: <a href="/contact-us" className='manrope f-12 w-500 lh-20 blue'>Contact us</a>,
    }
  ];

  const bundles = [
    {
      for: "For anyone",
      name: "Free Trial",
      type: "bundle",
      package_type: "free_trial_ai",
      desc: "See what's possible",
      price: "$0",
      price_monthly: "$0",
      price_time: "",
      price_details: "14-day limited trial",
      price_details_monthly: "14-day limited trial",
      discount_statement: "",
      discount_statement_monthly: "",
      switch_to_subscription: "",
      button_text: "Start your free trial",
      credits: "",
      credits_monthly: "",
      min_quantity: 0,
    },
    {
      for: "For self-serve users",
      name: "Basic Bundle",
      type: "bundle",
      package_type: "basic_ai",
      desc: "Flexible & easy-to-manage",
      price: "$49",
      price_monthly: "$49",
      price_time: "/final 4K image",
      price_details: "5 image minimum",
      price_details_monthly: "5 image minimum",
      discount_statement: "",
      discount_statement_monthly: "",
      switch_to_subscription: "Switch to subscription – save up to 27%",
      button_text: "Buy ",
      credits: "4 credits per image. All 20 credits available at once.",
      credits_monthly: "All 20 credits available at once. 4 credits per final image. Unlimited 1K generations.",
      min_quantity: 5,
      isPopular: true,
      credit_value: 4,
    },
    {
      for: "For low-touch sellers",
      name: "Studio Bundle",
      type: "bundle",
      package_type: "studio_bundle",
      desc: "Designer guided & managed",
      price: "$110",
      price_monthly: "$110",
      price_time: "/final 4K image",
      price_details: "45 image minimum",
      price_details_monthly: "45 image minimum",
      discount_statement: "",
      discount_statement_monthly: "",
      switch_to_subscription: "",
      button_text: "Buy",
      credits: "12 credits per image. All 540 credits available at once.",
      credits_monthly: "12 credits per image. 540 credits available at once.",
      min_quantity: 45,
      credit_value: 12,
    },
    {
      for: "For large teams",
      name: "ALL3D Enterprise",
      type: "bundle",
      package_type: "enterprise",
      desc: "Custom plan with 3D/AR",
      credits: "Contact us",
      price: "Let's talk",
      price_monthly: "Let's talk",
      price_details: "Schedule a time to discuss",
      price_details_monthly: "Schedule a time to discuss",
      discount_statement: "",
      discount_statement_monthly: "",
      switch_to_subscription: "",
      button_text: "Schedule a demo",
      credits: <a href="/contact-us" className='manrope f-12 w-500 lh-20 blue'>Contact us</a>,
      credits_monthly: <a href="/contact-us" className='manrope f-12 w-500 lh-20 blue'>Contact us</a>,
      min_quantity: 0,
      credit_value: 0,
    }
  ];

  const [plans, setPlans] = useState(bundles);
  const [quantity, setQuantity] = useState({});
  const [credits, setCredits] = useState({
    'basic_ai': 20,
    'studio_bundle': 540,
  });

  useEffect(() => {
    if (selectedPlan == 'subscriptions') {
      setPlans(subscriptions);
    } else {
      setPlans(bundles);
    }
  }, [selectedPlan]);

  const handleQuantityChange = (value, plan, credit_value) => {
    setQuantity((prev) => ({
      ...prev,
      [plan]: value,
    }));

    setCredits((prev) => ({
      ...prev,
      [plan]: (value || 0) * credit_value,
    }));
  };

  return (
    <div className="mobile-subscription-cards">
      {plans.map((plan, index) => (
        <div key={index} className="mobile-card-wrapper">
          <div key={index} className={`mobile-card ${plan.isPopular ? 'popular' : ''}`}>
            {plan.isPopular && (
              <div className="popular-tag">
                <img src="/fire.svg" alt="fire" />
                <span className="manrope f-10 w-700 white lh-20">POPULAR</span>
              </div>
            )}
            <div className="card-label mb-16 manrope f-14 w-500 lh-20 black-085">{plan.for}</div>
            <h3 className="mb-8 manrope f-20 w-700 lh-32 black-085">{plan.name}</h3>
            <p className="mb-16 manrope f-14 lh-22 w-500 black-085">{plan.desc}</p>

            {selectedPlan === "subscriptions" ?
              <>
                <div className="manrope f-26 w-700 lh-40 black-085 a-baseline">
                  {annual ? plan.price : plan.price_monthly}
                  <span className="manrope f-14 w-400 black-085 lh-22">{plan.price_time}</span>
                </div>
                <div className={`card-price-details manrope f-14 w-400 lh-22 ${['starter_ai', 'growth_ai', 'pro_ai'].includes(plan.package_type) ? 'mb-8' : 'mb-16'}`}>
                  {annual ? plan.price_details : plan.price_details_monthly}
                </div>
              </> :
              <div className={`justify-space-between align-end header-flex-no-wrap`}>
                <div>
                  <span className="manrope f-26 black-085 w-700 a-baseline" style={{ letterSpacing: '-1px' }}>
                    {plan.price}
                    <span className="manrope f-14 w-400 black-085 lh-22">{plan.price_time}</span>
                  </span>
                  <span className={`monthly-period manrope f-14 w-400 lh-22 mb-0`}>{plan.price_details}</span>
                </div>
                {!['enterprise', 'free_trial_ai'].includes(plan.package_type) && plan.type == 'bundle' &&
                  <div className="justify-in-end">
                    <div className="manrope f-10 lh-22 black-085 w-500 mr-8">QTY</div>
                    <InputNumber
                      onKeyDown={(e) => {
                        if ((["-", ".", " "].includes(e.key)) || (/[a-zA-Z\s]/.test(e.key) && !allowedKeys.includes(e.key))) {
                          e.preventDefault();
                        }
                      }}
                      min={plan.min_quantity}
                      value={quantity[plan.package_type] || plan.min_quantity}
                      onChange={(value) => handleQuantityChange(value, plan.package_type, plan.credit_value)}
                      className="manrope f-14 lh-22 black-085 w-400 width-64" />
                  </div>}
              </div>}
            {plan.discount_statement != "" ? (
              <div className="discount-wrapper">
                <TagFilled className="discount-icon" />
                <span className="discount manrope f-12 w-500">{annual ? plan.discount_statement : plan.discount_statement_monthly}</span>
              </div>
            ) : ''}
            {plan.switch_to_subscription != "" ? (
              <div className="switch-to-subscription-wrapper mt-8 justify-in-center">
                <a onClick={() => setSelectedPlan("subscriptions")}
                  className="manrope lh-20 w-500 f-12 w-500 blue">{plan.switch_to_subscription}</a>
              </div>
            ) : ''}
            <div>
              <Button
                className={`mt-16 ${['free_trial_ai'].includes(plan.package_type) ? 'free-trial-button' : ''} card-button manrope f-16 w-700 lh-24`}
                block
                onClick={() => {
                  if (['enterprise'].includes(plan.package_type))
                    window.open(`${ENVIRONMENT.CALENDLY_LINK}`, "_blank")
                  else if (['free_trial_ai'].includes(plan.package_type))
                    window.open(`${ENVIRONMENT.FREE_TRIAL_LINK}`, "_blank")
                  else
                    window.open(`${ENVIRONMENT.PORTAL_BASE_URL}/register?redirectURL=${encodeURIComponent(`/select-subscription?plan=${annual && plan.type === 'subscription' ? 'annual_' + plan.package_type : plan.package_type}&plan_type=${plan.type}${plan.type === 'bundle' ? `&quantity=${quantity[plan.package_type] || plan.min_quantity}` : ''}&utm_source=${utm_source}`)}`, "_blank")
                }}>
                {plan.button_text} {credits[plan.package_type] ? ` ${credits[plan.package_type]} credits` : ''}
              </Button>
            </div>

            <div className="justify-in-center mt-8">
              {plan.name != 'Free Trial' && (
                  plan.credits != "" && plan.type == 'subscription' ?
                <div className="manrope f-12 lh-20 w-400 black-085 text-center">{annual ? plan.credits : plan.credits_monthly}</div>
                : plan.package_type !== 'enterprise' ? <div className="manrope f-12 lh-20 w-400 black-085 text-center">{`All ${plan.credit_value * (quantity[plan.package_type] || plan.min_quantity)} credits available at once. ${plan.credit_value} credits per image. Unlimited 1K generations.`}</div> : plan.credits)}
            </div>

          </div>
          <div className={`mobile-features-section ${!expandedCards[index] ? 'expanded-radius' : 'expanded-radius'} ${plan.isPopular ? 'popular-tag-border' : ''}`}>
            <div
              className={`mobile-features-header ${expandedCards[index] ? `expanded-border` : ''}`}
              onClick={() => toggleCard(index)}>
              <span className="manrope f-14 w-700 lh-22 black-085">Includes:</span>
              <div className={`expand-arrow ${expandedCards[index] ? 'expanded' : ''}`}>
                <UpOutlined className="manrope f-14 w-700 lh-22 black-085" />
              </div>
            </div>

            {expandedCards[index] &&
              <div className="mobile-features-content">
                <MobileFeaturesList selectedPlan={plan.package_type} quantity={quantity} />
                <div className="advanced-features-header">
                  <img src="/ai_creator/ai-star-white.svg" alt="sparkle" />
                  <span className="manrope f-12 w-700 lh-22 white">Plus, these advanced editing tools and features</span>
                </div>
                <MobileAdvancedFeaturesList selectedPlan={plan.package_type} />
              </div>}
          </div>
        </div>
      ))}
      {selectedPlan === "subscriptions" ?
        <div className="text-left manrope f-14 black-085 w-500 lh-22">
          All subscriptions are annual contracts which will automatically renew each year. You may cancel at any time before the renewal date.{" "}
          <a href="#faqs" className="manrope f-14 w-700 blue lh-22">See FAQs</a>
        </div> : <div className="text-left manrope f-14 black-085 w-500 lh-22">
          All bundles are a one-time purchase and use a credit system. More credits can be purchased at any time.{" "}
          <a href="#faqs" className="manrope f-14 w-700 blue lh-22">See FAQs</a>
        </div>}
    </div>
  );
};

const MobileFeaturesList = ({ selectedPlan, quantity }) => {
  let features = {
    "free_trial_ai": [
      { name: "Product uploads", value: "3" },
      { name: "Image generations", value: "12" },
      { name: "Final 4K images", value: "1" },
      { name: "User licenses", value: "1" },
      { name: "Asset management", included: true },
      { name: "Sharing & cloud storage", included: false },
      { name: "Live onboarding session", included: false },
      { name: "Managed design services", included: false },
      { name: "Dedicated support", included: false },
      { name: "Account manager", included: false }
    ],
    "basic_ai": [
      { name: "Product uploads", value: "Unlimited" },
      { name: "Image generations", value: "Unlimited" },
      { name: "Final 4K images", value: quantity?.basic_ai || 5 },
      { name: "User licenses", value: "2" },
      { name: "Asset management", included: true },
      { name: "Sharing & cloud storage", included: true },
      { name: "Live onboarding session", included: true },
      { name: "Managed design services", included: false },
      { name: "Dedicated support", included: false },
      { name: "Account manager", included: false }
    ],
    "studio_bundle": [
      { name: "Product uploads", value: "Unlimited" },
      { name: "Image generations", value: "Unlimited" },
      { name: "Final 4K images", value: quantity?.studio_bundle || 45 },
      { name: "User licenses", value: "2" },
      { name: "Asset management", included: true },
      { name: "Sharing & cloud storage", included: true },
      { name: "Live onboarding session", included: true },
      { name: "Managed design services", included: true },
      { name: "Dedicated support", included: true },
      { name: "Account manager", included: false }
    ],
    "starter_ai": [
      { name: "Product uploads", value: "Unlimited" },
      { name: "Image generations", value: "Unlimited" },
      { name: "Final 4K images", value: "240 annually" },
      { name: "User licenses", value: "2" },
      { name: "Asset management", included: true },
      { name: "Sharing & cloud storage", included: true },
      { name: "Live onboarding session", included: true },
      { name: "Managed design services", included: false },
      { name: "Dedicated support", included: false },
      { name: "Account manager", included: false }
    ],
    "growth_ai": [
      { name: "Product uploads", value: "Unlimited" },
      { name: "Image generations", value: "Unlimited" },
      { name: "Final 4K images", value: "300 annually" },
      { name: "User licenses", value: "2" },
      { name: "Asset management", included: true },
      { name: "Sharing & cloud storage", included: true },
      { name: "Live onboarding session", included: true },
      { name: "Managed design services", included: false },
      { name: "Dedicated support", included: false },
      { name: "Account manager", included: false }
    ],
    "pro_ai": [
      { name: "Product uploads", value: "Unlimited" },
      { name: "Image generations", value: "Unlimited" },
      { name: "Final 4K images", value: "600 annually" },
      { name: "User licenses", value: "2" },
      { name: "Asset management", included: true },
      { name: "Sharing & cloud storage", included: true },
      { name: "Live onboarding session", included: true },
      { name: "Managed design services", included: false },
      { name: "Dedicated support", included: false },
      { name: "Account manager", included: false }
    ],
    "enterprise": [
      { name: "Product uploads", value: "Unlimited" },
      { name: "Image generations", value: "Unlimited" },
      { name: "Final 4K images", value: "Custom" },
      { name: "User licenses", value: "Multiple" },
      { name: "Asset management", included: true },
      { name: "Sharing & cloud storage", included: true },
      { name: "Live onboarding session", included: true },
      { name: "Managed design services", included: true },
      { name: "Dedicated support", included: true },
      { name: "Account manager", included: true }
    ]
  };

  return (
    <div className="mobile-features-list">
      {features[selectedPlan].map((feature, index) => (
        <div key={index} className="feature-row">
          <span className="feature-name manrope f-14 w-700 lh-22 black-085">{feature.name}</span>
          <span className="feature-value justify-in-center">
            {feature.value ? (
              <span className="manrope f-14 w-500 lh-22 black-085 text-center">{feature.value}</span>
            ) : feature.included ? (
              <div className="checkmark">
                <CheckOutlined className="manrope f-12 w-700 white" />
              </div>
            ) : (
              <div className="empty-cell"></div>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};

const MobileAdvancedFeaturesList = ({ selectedPlan }) => {
  let features = {
    "free_trial_ai": [
      { name: "Resize product", included: true },
      { name: "Magic erase", included: true },
      { name: "Replace items", included: true },
      { name: "Upload inspiration", included: true },
      { name: "Upload props", included: true },
    ],
    "basic_ai": [
      { name: "Resize product", included: true },
      { name: "Magic erase", included: true },
      { name: "Replace items", included: true },
      { name: "Upload inspiration", included: true },
      { name: "Upload props", included: true },
    ],
    "studio_bundle": [
      { name: "Resize product", included: true },
      { name: "Magic erase", included: true },
      { name: "Replace items", included: true },
      { name: "Upload inspiration", included: true },
      { name: "Upload props", included: true },
    ],
    "starter_ai": [
      { name: "Resize product", included: true },
      { name: "Magic erase", included: true },
      { name: "Replace items", included: true },
      { name: "Upload inspiration", included: true },
      { name: "Upload props", included: true },
    ],
    "growth_ai": [
      { name: "Resize product", included: true },
      { name: "Magic erase", included: true },
      { name: "Replace items", included: true },
      { name: "Upload inspiration", included: true },
      { name: "Upload props", included: true },

    ],
    "pro_ai": [
      { name: "Resize product", included: true },
      { name: "Magic erase", included: true },
      { name: "Replace items", included: true },
      { name: "Upload inspiration", included: true },
      { name: "Upload props", included: true },
    ],
    "enterprise": [
      { name: "Resize product", included: true },
      { name: "Magic erase", included: true },
      { name: "Replace items", included: true },
      { name: "Upload inspiration", included: true },
      { name: "Upload props", included: true },
      { name: "Photorealistic 3D models", included: true },
      { name: "AR view in room", included: true },
      { name: "Product silo images", included: true },
      { name: "3D CGI images", included: true },
      { name: "Custom scenes", included: true },
      { name: "API integration", included: true },
      { name: "3D configurator", included: true },
      { name: "Project management", included: true },
      { name: "Team collaboration", included: true },
    ]
  };

  return (
    <div className="mobile-features-list">
      {features[selectedPlan].map((feature, index) => (
        feature.included && <div key={index} className={`feature-row`}>
          <span className="feature-name manrope f-14 w-700 lh-22 black-085">{feature.name}</span>
          <span className="feature-value justify-in-center">
            {feature.value ? (
              <span className="manrope f-14 w-500 lh-22 black-085 text-center">{feature.value}</span>
            ) : feature.included ? (
              <div className="checkmark">
                <CheckOutlined className="manrope f-12 w-700 white" />
              </div>
            ) : ''}
          </span>
        </div>
      ))}
    </div>
  );
};

const SubscriptionSelector = ({ selectedPlan, setSelectedPlan, annual, setAnnual }) => {
  return (
    <div className={`subscription-selector-wrapper ${selectedPlan === "subscriptions" ? "justify-space-between" : "justify-in-center"}`}>
      {selectedPlan === "subscriptions" &&
        <div style={{ width: 250 }} className="hide-web-version">

        </div>}
      <div className="subscription-selector">
        <div
          className={`plan ${selectedPlan === "bundles" ? "selected" : ""}`}
          onClick={() => setSelectedPlan("bundles")}>
          <span className="manrope f-14 w-700 lh-22">Buy a Bundle</span>
        </div>
        <div
          className={`plan ${selectedPlan === "subscriptions" ? "selected" : ""}`}
          onClick={() => setSelectedPlan("subscriptions")}>
          <span className=" manrope f-14 w-700 lh-22">Subscribe & Save</span>
        </div>
      </div>
      {selectedPlan === "subscriptions" &&
        <div className="switch-wrapper">
          <span className={`manrope f-12 lh-20 black-085 ${annual ? 'w-500' : 'w-700'} mr-8`}>Monthly</span>
          <Switch
            checked={annual}
            onChange={(checked) => setAnnual(checked)} />
          <span className={`manrope f-12 lh-20 black-085 ${annual ? 'w-700' : 'w-500'} ml-8`}>Annually</span>
          <div className={`ml-8 ${annual ? 'saver-bg blue' : 'saver-bg gray'}`}>
            <span className={`manrope f-12 lh-20 ${annual ? 'w-700 blue-18' : 'w-500 black-085'}`}>Save 17%</span>
          </div>
        </div>}
    </div>
  );
};


const Plans = () => {

  return (
    <>
      <WebVersion />
      <MobileVersion />
    </>
  );
};

const MobileVersion = () => {
  const [selectedPlan, setSelectedPlan] = useState("bundles");
  const [annual, setAnnual] = useState(true);
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`pricing-plans-wrapper ${expanded ? 'expand-height' : ''} hide-mobile-version`}>
      <div className="pricing-plans">
        <span className="manrope f-30 w-600 lh-40 mb-20 heading-pricing">
          Flexible pricing to bring your listings to life with accurate AI control
        </span>
        <Paragraph className="description manrope f-16 w-500 lh-24">
          Find the perfect plan to create accurate, brand-specific images to showcase your products.
        </Paragraph>
        <SubscriptionSelector
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          annual={annual}
          setAnnual={setAnnual}
        />
      </div>

      <div className="mobile-pricing-cards">
        <MobilePackageCards expanded={expanded} setExpanded={setExpanded} annual={annual} selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} />
      </div>
    </div>
  );
};

const WebVersion = () => {
  const [selectedPlan, setSelectedPlan] = useState("bundles");
  const [annual, setAnnual] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handlePlanChange = (newPlan) => {
    if (newPlan !== selectedPlan) {
      setIsTransitioning(true);
      setTimeout(() => {
        setSelectedPlan(newPlan);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 20);
      }, 100);
    }
  };

  return (
    <div className="pricing-plans-wrapper hide-web-version">
      <div className="pricing-plans">
        <span className="manrope f-50 w-600 lh-60 mb-20 heading-pricing">
          Flexible pricing to bring your listings to life with accurate AI control
        </span>
        <Paragraph className="description manrope f-16 w-500 lh-24">
          Find the perfect plan to create accurate, brand-specific images to showcase your products.
        </Paragraph>
        <SubscriptionSelector
          selectedPlan={selectedPlan}
          setSelectedPlan={handlePlanChange}
          annual={annual}
          setAnnual={setAnnual}
        />

        <div className={`content-transition-wrapper ${isTransitioning ? 'transitioning' : ''}`}>
          {selectedPlan === "subscriptions" ? (
            <>
              <FeatureComparisonTable selectedPlan="subscriptions" annual={annual} setSelectedPlan={handlePlanChange} />
              <Paragraph className="footnote manrope f-14 black-085 w-500 lh-22">
                All subscriptions are annual contracts which will automatically renew each year. You may cancel at any time before the renewal date.{" "}
                <a href="#faqs" className="footnote-link manrope f-14 w-700">See FAQs</a>
              </Paragraph>
            </>
          ) : (
            <>
              <FeatureComparisonTable selectedPlan="bundles" annual={annual} setSelectedPlan={handlePlanChange} />
              <Paragraph className="footnote manrope f-14 black-085 w-500 lh-22">
                All bundles are a one-time purchase and use a credit system. More credits can be purchased at any time.{" "}
                <a href="#faqs" className="footnote-link manrope f-14 w-700">See FAQs</a>
              </Paragraph>
            </>
          )}
        </div>

      </div>

    </div>
  );
};

export default Plans;
